import { inject, Injectable } from '@angular/core';
import {
  BehaviorSubject,
  catchError,
  combineLatest,
  EMPTY,
  map,
  Observable,
  switchMap,
  take,
} from 'rxjs';
import {
  AddressService,
  Definition,
  DetailItemService,
  ExtendedDetailItem,
  ProfileData,
  ProfileDetails,
  ProfileDetailsApi,
  ProfileDetailsKey,
  SearchRequestType,
} from '@ireckonu/shared-ui';

import { ProfilesService } from './profiles.service';

const DEFAULT_ATTRIBUTES_LIST = [
  ProfileDetailsKey.Person,
  ProfileDetailsKey.Name,
  ProfileDetailsKey.Address,
  ProfileDetailsKey.Email,
  ProfileDetailsKey.Attribute,
  ProfileDetailsKey.PhoneNumber,
  ProfileDetailsKey.PersonExternalReference,
  ProfileDetailsKey.Preference,
  ProfileDetailsKey.PaymentCard,
  ProfileDetailsKey.AdditionalProfile,
  ProfileDetailsKey.AdditionalEmail,
  ProfileDetailsKey.AdditionalAddress,
  ProfileDetailsKey.AdditionalPhoneNumber,
  //   ProfileDetailsKey.Document, // TODO: check if we have this key
  ProfileDetailsKey.CustomerLoyalty,
  ProfileDetailsKey.Social,
  ProfileDetailsKey.LoyaltyLevel,
  ProfileDetailsKey.DataConsent,
];

const CUSTOM_TYPES: SearchRequestType<ProfileDetailsKey>[] = [
  {
    Type: ProfileDetailsKey.Email,
  },
  {
    Type: ProfileDetailsKey.Name,
  },
  {
    Type: ProfileDetailsKey.Person,
  },
  {
    Type: ProfileDetailsKey.PhoneNumber,
  },
  {
    Type: ProfileDetailsKey.Address,
  },
  {
    Type: ProfileDetailsKey.Attribute,
  },
  {
    Type: ProfileDetailsKey.PersonExternalReference,
  },
  {
    Type: ProfileDetailsKey.Social,
  },
  {
    Type: ProfileDetailsKey.CustomerLoyalty,
  },
  {
    Type: ProfileDetailsKey.GlobalStay,
  },
  {
    Type: ProfileDetailsKey.Rfm,
  },
  {
    Type: ProfileDetailsKey.PaymentCard,
  },
  {
    Type: ProfileDetailsKey.LoyaltyLevel,
  },
  {
    Type: ProfileDetailsKey.AdditionalProfile,
  },
  {
    Type: ProfileDetailsKey.AdditionalEmail,
  },
  {
    Type: ProfileDetailsKey.AdditionalPhoneNumber,
  },
  {
    Type: ProfileDetailsKey.AdditionalAddress,
  },
  {
    Type: ProfileDetailsKey.Preference,
  },
  {
    Type: ProfileDetailsKey.DataConsent,
  },
  {
    Type: ProfileDetailsKey.GuestPreference,
  },
  //   {
  //     Type: ProfileDetailsKey.Document,
  //   },
];

@Injectable({ providedIn: 'root' })
export class ProfileDetailsService implements ProfileDetailsApi {
  private readonly profileIdChange = new BehaviorSubject(0);
  private readonly personDataChange =
    new BehaviorSubject<ProfileDetails | null>(null);

  private readonly profilesService = inject(ProfilesService);
  private readonly detailItemService = inject(DetailItemService);
  private readonly addressService = inject(AddressService);

  // TODO: delete it when BE is ready
  private isUiTabsConfigured(): Observable<boolean> {
    return this.profilesService
      .getDefinitions()
      .pipe(map(this.isDefinitionsContainUiTabs));
  }

  private isDefinitionsContainUiTabs(definitions: Definition[]): boolean {
    return definitions.some((def) => !!def.UiTabs?.[0]?.Code);
  }

  onProfileIdChange(): Observable<number> {
    return this.profileIdChange.asObservable();
  }

  onPersonDataChange(): Observable<ProfileDetails | null> {
    return this.personDataChange.asObservable();
  }

  set currentProfileId(id: number) {
    this.personDataChange.next(null);
    this.profileIdChange.next(id);
    this.buildForm().subscribe();
  }

  // required only for Loyalty
  updatePersonData(customTypes?: SearchRequestType<ProfileDetailsKey>[]): void {
    this.buildForm(customTypes).subscribe();
  }

  private buildForm(
    customTypes?: SearchRequestType<ProfileDetailsKey>[],
  ): Observable<void> {
    return combineLatest([
      this.profilesService.getDefinitions(),
      this.isUiTabsConfigured().pipe(
        switchMap((configured) => {
          if (!customTypes && !configured) {
            customTypes = CUSTOM_TYPES;
          }
          return this.profilesService.searchProfilesById(
            this.profileIdChange.value,
          );
        }),
      ),
    ]).pipe(
      take(1),
      map(([definitions, resp]) => {
        const types = definitions.map((def) => def.Type);

        if (!resp.Data || resp.Data.length === 0) {
          return;
        }

        let personData: ProfileDetails;
        if (customTypes) {
          personData = {
            ...this.personDataChange.value,
            ...resp.Data[0].Data,
          };
        } else {
          personData = resp.Data[0].Data;
        }

        if (customTypes) {
          customTypes.forEach((type) => {
            const key = type.Type;
            personData[key] = resp.Data[0].Data[key] || [];
          });
        }

        const isUiTabsConfigured = this.isDefinitionsContainUiTabs(definitions);
        const attributeList: ProfileDetailsKey[] = isUiTabsConfigured
          ? definitions.map((def) => def.Type)
          : DEFAULT_ATTRIBUTES_LIST;

        attributeList.forEach((name) => {
          if (types.includes(name)) {
            const key = name;
            if (personData[key]?.length) {
              this.detailItemService.mergeDefinitionAndDetails<ProfileDetailsKey>(
                definitions,
                personData[key],
                name,
              );
              personData[key][0].UiTabs =
                definitions.find((def) => def.Type === name)?.UiTabs || [];
            } else {
              this.createSkeletonObject(definitions, name, personData);
            }
          }
        });

        this.personDataChange.next(personData);
      }),
      catchError(() => {
        this.personDataChange.next(null);
        return EMPTY;
      }),
    );
  }

  private createSkeletonObject(
    definitions: Definition<ProfileDetailsKey>[],
    itemType: ProfileDetailsKey,
    personData: ProfileDetails,
  ): void {
    const object: Definition | undefined = definitions.find(
      (def) => def.Type === itemType,
    );
    if (!object) {
      return;
    }
    const formObject = this.detailItemService.createNewFormObject(object);

    const isCreateAllowed = object.AllowedActions?.includes('Create');
    if (!isCreateAllowed) {
      formObject.tableShown = false;
    }

    personData[itemType] = [
      formObject,
    ] as ExtendedDetailItem<ProfileDetailsKey>[];
  }

  getProfileDataFromDTO(personData: ProfileDetails): ProfileData {
    return {
      Title:
        this.detailItemService.getDetailsValue('Title', personData.Person) ||
        '',
      Firstname:
        this.detailItemService.getDetailsValue(
          'Firstname',
          personData.Person,
        ) || 'N/A',
      Lastname:
        this.detailItemService.getDetailsValue('Lastname', personData.Person) ||
        'N/A',
      KatakanaFirstname: personData.Name?.find((name) =>
        name.Properties.find(
          (p) => p.Name === 'TypeCode' && p.Value === 'Katakana',
        ),
      )?.Properties.find((p) => p.Name === 'FirstName')?.Value as string,
      KatakanaLastname: personData.Name?.find((name) =>
        name.Properties.find(
          (p) => p.Name === 'TypeCode' && p.Value === 'Katakana',
        ),
      )?.Properties.find((p) => p.Name === 'LastName')?.Value as string,
      KanjiFirstname: personData.Name?.find((name) =>
        name.Properties.find(
          (p) => p.Name === 'TypeCode' && p.Value === 'Kanji',
        ),
      )?.Properties.find((p) => p.Name === 'FirstName')?.Value as string,
      KanjiLastname: personData.Name?.find((name) =>
        name.Properties.find(
          (p) => p.Name === 'TypeCode' && p.Value === 'Kanji',
        ),
      )?.Properties.find((p) => p.Name === 'LastName')?.Value as string,
      Email:
        this.detailItemService.getDetailsPrimaryValue(
          ['EmailAddress', 'Address'],
          personData.Email,
        ) || '',
      Prefix:
        this.detailItemService.getDetailsValue('Prefix', personData.Name) || '',
      Gender:
        this.detailItemService.getDetailsValue(
          'GenderCode',
          personData.Person,
        ) || 'N/A',
      PhoneNumber:
        this.detailItemService.getDetailsPrimaryValue(
          'Number',
          personData.PhoneNumber,
        ) || 'N/A',
      PhoneValidityCode:
        this.detailItemService.getDetailsPrimaryValue(
          'ValidityStatusCode',
          personData.PhoneNumber,
        ) || null,
      EmailValidityCode:
        this.detailItemService.getDetailsPrimaryValue(
          'ValidityStatusCode',
          personData.Email,
        ) || null,
      CountryAccessCode:
        this.detailItemService.getDetailsValue(
          'CountryAccessCode',
          personData.PhoneNumber,
        ) || null,
      Address:
        this.addressService.getAddressLine(
          this.detailItemService.buildAddress(personData.Address),
        ) || 'N/A',
      NotParsedAddress:
        this.detailItemService.buildAddress(personData.Address) || null,
      //   ProfileImage: this.profileImages,
      IruId:
        this.detailItemService.getDetailsValue('Id', personData.Person) ||
        'N/A',
      MemberId:
        this.detailItemService.getDetailsPrimaryValue(
          'MembershipId',
          personData.CustomerLoyalty,
        ) ||
        this.detailItemService.getDetailsValue(
          'MembershipId',
          personData.CustomerLoyalty,
        ) ||
        'N/A',
      LoyaltyLevel:
        this.detailItemService.getDetailsPrimaryValue(
          'LevelCode',
          personData.CustomerLoyalty,
        ) ||
        this.detailItemService.getDetailsValue(
          'LevelCode',
          personData.CustomerLoyalty,
        ) ||
        'N/A',
      Facts:
        this.detailItemService.getDetailsValue(
          'Value',
          personData.Preference,
        ) || 'N/A',
      RFMTier:
        this.detailItemService.getDetailsValue('Value', personData.Rfm) ||
        this.buildActiveLoyaltyLevelTier(personData) ||
        null,
      RFMScore:
        this.detailItemService.getDetailsValue('Score', personData.Rfm) ||
        this.buildActiveLoyaltyLevelScore(personData) ||
        null,
      NumberOfStays:
        this.detailItemService.getDetailsValue(
          'NumberOfStays',
          personData.GlobalStay,
        ) || 'N/A',
      NumberOfNights:
        this.detailItemService.getDetailsValue(
          'NumberOfNights',
          personData.GlobalStay,
        ) || 'N/A',
      HotelDiversity:
        this.detailItemService.getDetailsValue(
          'HotelDiversity',
          personData.GlobalStay,
        ) || 'N/A',
      LastVisit:
        this.detailItemService.getDetailsValue(
          'LastStayDate',
          personData.GlobalStay,
        ) || null,
      TotalSpent:
        this.detailItemService.getDetailsValue(
          'StayTotalRevenue',
          personData.GlobalStay,
        ) || null,
      CurrencyCode:
        this.detailItemService.getDetailsValue(
          'CurrencyCode',
          personData.GlobalStay,
        ) || null,
      LastHotel:
        this.detailItemService.getDetailsValue(
          'LastStayHotelCode',
          personData.GlobalStay,
        ) || null,
      GuaranteedTierValue:
        this.detailItemService.getDetailsValue(
          'GuaranteedTier',
          personData.Rfm,
        ) || null,
      Language:
        this.detailItemService.getDetailsValue(
          'LanguageCode',
          personData.Person,
        ) || 'N/A',
      LanguageCode:
        (personData.Person[0]?.Properties.find((p) => p.Name === 'LanguageCode')
          ?.Value as string) || null,
      Nationality:
        this.detailItemService.getDetailsValue(
          'CountryCode',
          personData.Person,
        ) || 'N/A',
      CreatedDateTimeUtc:
        this.detailItemService.getDetailsValue(
          ['CreatedDateTimeUtc', 'CreatedDateTime'],
          personData.Person,
        ) || null,
      ChangedDateTimeUtc:
        this.detailItemService.getDetailsValue(
          ['ChangedDateTimeUtc', 'LastUpdateDateTime'],
          personData.Person,
        ) || null,
      Source:
        this.detailItemService.getDetailsValue('Source', personData.Person) ||
        'N/A',
      DateOfBirth:
        this.detailItemService.getDetailsValue(
          'DateOfBirth',
          personData.Person,
        ) || null,
    } as ProfileData;
  }

  sanitizeProfileData(profile: ProfileData): ProfileData {
    return Object.fromEntries(
      Object.entries(profile).map(([key, value]) => [
        key,
        typeof value === 'string' && value === 'N/A' ? '' : value,
      ]),
    ) as ProfileData;
  }

  private buildActiveLoyaltyLevelTier(
    personData: ProfileDetails,
  ): string | null {
    const activeLoyaltyLevel = this.getActiveLoyaltyLevel(personData);
    if (activeLoyaltyLevel?.Properties) {
      const tier = activeLoyaltyLevel.Properties.find(
        (prop) => prop.Name === 'LevelCode',
      );
      return tier ? (tier.Value as string) : null;
    }
    return null;
  }

  private buildActiveLoyaltyLevelScore(
    personData: ProfileDetails,
  ): number | null {
    const activeLoyaltyLevel = this.getActiveLoyaltyLevel(personData);
    if (activeLoyaltyLevel?.Properties) {
      const score = activeLoyaltyLevel.Properties.find(
        (prop) => prop.Name === 'Score',
      );
      return score ? (score.Value as number) : null;
    }
    return null;
  }

  private getActiveLoyaltyLevel(
    personData: ProfileDetails,
  ): ExtendedDetailItem<ProfileDetailsKey> {
    if (personData.LoyaltyLevel) {
      return (
        personData.LoyaltyLevel.find((item) => {
          const isActiveProp = item.Properties?.find(
            (prop) => prop.Name === 'IsActive',
          );
          return isActiveProp?.Value === true;
        }) ?? ({} as ExtendedDetailItem<ProfileDetailsKey>)
      );
    }

    return {} as ExtendedDetailItem<ProfileDetailsKey>;
  }
}
